import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Loader2, FolderKanban, CheckCircle2, Clock, IndianRupee, Search, SlidersHorizontal, MoreVertical, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";
import { useAuth } from "@/context/AuthContext";
import { PageBreadcrumb } from "@/components/PageBreadcrumb";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { FieldError } from "@/components/FieldError";
import { fmtDate, partyLabel } from "@/lib/format";
import { getErrorMessage, getFieldErrors } from "@/lib/errors";
import { CompanyField } from "@/components/CompanyField";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 10;
const ALL = "__all";

const inr = (v) => `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
// ₹2.3 Cr / ₹45.0 L / ₹9,500 — the compact form used on the summary card.
function inrCompact(v) {
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(1)} Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(1)} L`;
  return inr(v);
}
const two = (n) => String(n).padStart(2, "0");

const STAT_TONES = {
  blue:   { card: "from-blue-50 to-blue-100/70",       icon: "text-blue-600",    wave: "text-blue-200" },
  green:  { card: "from-emerald-50 to-emerald-100/70", icon: "text-emerald-500", wave: "text-emerald-200" },
  orange: { card: "from-orange-50 to-amber-100/70",    icon: "text-orange-500",  wave: "text-orange-200" },
  purple: { card: "from-violet-50 to-violet-100/70",   icon: "text-violet-700",  wave: "text-violet-200" },
};

function StatCard({ tone, icon: Icon, value, label, testId }) {
  const t = STAT_TONES[tone];
  return (
    <div className={cn("relative flex items-center gap-4 overflow-hidden rounded-2xl border border-white/80 bg-gradient-to-br p-5 shadow-sm", t.card)} data-testid={testId}>
      <div className={cn("flex h-16 w-16 flex-none items-center justify-center rounded-2xl bg-white/70 shadow-sm ring-1 ring-white", t.icon)}>
        <Icon className="h-8 w-8" />
      </div>
      <div className="min-w-0">
        <div className="text-3xl font-bold leading-none tracking-tight text-foreground">{value}</div>
        <div className="mt-1.5 text-sm text-foreground/70">{label}</div>
      </div>
      <svg className={cn("pointer-events-none absolute -bottom-1 -right-1 h-3/5 w-1/2 opacity-60", t.wave)} viewBox="0 0 120 60" preserveAspectRatio="none" aria-hidden="true">
        <path d="M0 60C30 55 40 20 70 28s30-16 50-24V60z" fill="currentColor" />
      </svg>
    </div>
  );
}

// PLANNING reads blue as in the design; every other status keeps the app-wide pill.
function ProjectStatus({ value }) {
  if (value !== "PLANNING") return <StatusPill value={value} />;
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-blue-200 bg-blue-50 px-2.5 py-0.5 text-[11px] font-semibold text-blue-700">
      <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-blue-500" />PLANNING
    </span>
  );
}

export default function AllProjects() {
  const { isOrgAdmin } = useAuth();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: projectData }, { data: companyData }] = await Promise.all([
        api.get("/projects"), api.get("/masters/companies"),
      ]);
      setProjects(projectData);
      setCompanies(companyData);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { setPage(1); }, [search, statusFilter]);

  const companyName = (id) => companies.find((c) => c.id === id)?.name || "—";
  const statuses = [...new Set(projects.map((p) => p.status).filter(Boolean))];
  const q = search.trim().toLowerCase();
  const visible = projects.filter((p) =>
    (!statusFilter || p.status === statusFilter) &&
    (!q || `${p.code} ${p.name} ${companyName(p.company_id)}`.toLowerCase().includes(q)));

  const pages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const rows = visible.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const activeCount = projects.filter((p) => p.status === "ACTIVE").length;
  const planningCount = projects.filter((p) => p.status === "PLANNING").length;
  const totalValue = projects.reduce((sum, p) => sum + (Number(p.contract_value) || 0), 0);
  const filtered = Boolean(search || statusFilter);

  return (
    <div className="space-y-6" data-testid="all-projects-page">
      <div className="relative overflow-hidden" data-tour="page-title">
        {/* site-skyline backdrop, purely decorative */}
        <svg className="pointer-events-none absolute right-0 top-0 hidden h-56 w-[640px] text-slate-400 opacity-20 md:block" viewBox="0 0 640 224" aria-hidden="true">
          <g fill="currentColor"><rect x="330" y="56" width="100" height="168" /><rect x="440" y="20" width="110" height="204" /><rect x="560" y="84" width="80" height="140" /></g>
          <g stroke="currentColor" strokeWidth="3" fill="none"><path d="M250 224V24M180 30h180M350 30v22M196 30l54-16 54 16" /></g>
        </svg>
        <div className="relative space-y-3">
          <PageBreadcrumb className="text-sm" />
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1 className="text-4xl font-bold tracking-tight text-foreground">All Projects</h1>
              <p className="mt-1 text-base text-foreground/70">Manage and monitor your construction projects.</p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {(isOrgAdmin || can("PROJECT_LIST", "create")) && <CreateProjectDialog companies={companies} onCreated={load} />}
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4" data-testid="all-projects-stats">
        <StatCard tone="blue" icon={FolderKanban} value={two(projects.length)} label="Total Projects" testId="stat-total" />
        <StatCard tone="green" icon={CheckCircle2} value={two(activeCount)} label="Active" testId="stat-active" />
        <StatCard tone="orange" icon={Clock} value={two(planningCount)} label="Planning" testId="stat-planning" />
        <StatCard tone="purple" icon={IndianRupee} value={inrCompact(totalValue)} label="Total Contract Value" testId="stat-value" />
      </div>

      <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
        <div className="flex flex-col gap-3 px-6 pb-4 pt-5 lg:flex-row lg:items-center" data-testid="all-projects-toolbar">
          <div className="flex items-center gap-3">
            <span className="h-6 w-1 rounded-full bg-primary" />
            <h2 className="text-xl font-bold tracking-tight text-foreground">Project Directory</h2>
            <span className="rounded-full bg-muted px-3 py-1 text-xs font-semibold text-muted-foreground" data-testid="all-projects-pill">
              {visible.length} {visible.length === 1 ? "Project" : "Projects"}
            </span>
          </div>
          <div className="flex flex-1 flex-wrap items-center justify-end gap-3">
            <div className="relative w-full sm:w-60">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." className="h-10 rounded-xl pl-9" data-testid="all-projects-search" />
            </div>
            <Select value={statusFilter || ALL} onValueChange={(v) => setStatusFilter(v === ALL ? "" : v)}>
              <SelectTrigger className="h-10 w-40 rounded-xl" data-testid="all-projects-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All statuses</SelectItem>
                {statuses.map((v) => <SelectItem key={v} value={v}>{v.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button type="button" variant="outline" size="icon" className="h-10 w-10 rounded-xl" title={filtered ? "Clear filters" : "No filters applied"} disabled={!filtered}
              onClick={() => { setSearch(""); setStatusFilter(""); }} data-testid="all-projects-clear-filters">
              <SlidersHorizontal className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {!loading && projects.length === 0 ? (
          <EmptyState title="No projects yet" description="Create your first project to get started." testId="all-projects-empty-state" />
        ) : (
          <div className="px-3">
            <Table>
              <TableHeader>
                <TableRow className="border-0 bg-muted/60 hover:bg-muted/60 [&>th:first-child]:rounded-l-lg [&>th:last-child]:rounded-r-lg">
                  {["Code", "Name", "Company", "Contract Value", "Start", "Target End", "Status"].map((h) => (
                    <TableHead key={h} className="h-11 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{h}</TableHead>
                  ))}
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((p) => (
                  <TableRow key={p.id} className="cursor-pointer" onClick={() => navigate(`/projects/${p.id}`)} data-testid={`project-row-${p.code}`}>
                    <TableCell><span className="rounded-md bg-muted px-2.5 py-1.5 text-sm font-bold text-foreground">{p.code}</span></TableCell>
                    <TableCell className="font-semibold text-foreground">{p.name}</TableCell>
                    <TableCell className="text-muted-foreground">{companyName(p.company_id)}</TableCell>
                    <TableCell className="font-semibold tabular-nums text-foreground">{p.contract_value ? inr(p.contract_value) : "—"}</TableCell>
                    <TableCell className="text-foreground/80">{fmtDate(p.start_date)}</TableCell>
                    <TableCell className="text-foreground/80">{fmtDate(p.target_end_date)}</TableCell>
                    <TableCell><ProjectStatus value={p.status} /></TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button type="button" variant="ghost" size="icon" className="h-8 w-8" aria-label={`Actions for ${p.code}`} data-testid={`project-actions-${p.code}`}><MoreVertical className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/projects/${p.id}`)}><ExternalLink className="mr-2 h-4 w-4" /> Open project</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && visible.length === 0 && (
                  <TableRow><TableCell colSpan={8} className="py-10 text-center text-muted-foreground">No projects match your filters.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}

        {!loading && projects.length > 0 && (
          <div className="flex items-center justify-between px-6 py-4 text-sm text-muted-foreground" data-testid="all-projects-count">
            <span>Showing {rows.length} of {projects.length} projects</span>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" size="icon" className="h-9 w-9 rounded-full" disabled={current <= 1} onClick={() => setPage(current - 1)} aria-label="Previous page"><ChevronLeft className="h-4 w-4" /></Button>
              {Array.from({ length: pages }, (_, i) => i + 1).map((n) => (
                <Button key={n} type="button" size="icon" variant={n === current ? "default" : "outline"} className={cn("h-9 w-9", n === current ? "rounded-lg font-bold" : "rounded-full")} onClick={() => setPage(n)} aria-current={n === current ? "page" : undefined}>{n}</Button>
              ))}
              <Button type="button" variant="outline" size="icon" className="h-9 w-9 rounded-full" disabled={current >= pages} onClick={() => setPage(current + 1)} aria-label="Next page"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CreateProjectDialog({ companies, onCreated }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({
    code: "", name: "", company_id: "", client_id: "", contract_value: "",
    location: "", start_date: "", target_end_date: "", dc_capacity_mwp: "", wbs_template_id: "__default",
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  useEffect(() => {
    if (open) api.get("/revenue/clients").then(({ data }) => setClients(data)).catch(() => {});
  }, [open]);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get("/wbs-templates").then(({ data }) => {
      setTemplates(data);
      const preferred = data.find((t) => t.name === "Buildings / Mall / Residential") || data.find((t) => /building/i.test(t.name)) || data[0];
      if (preferred) setForm((f) => (f.wbs_template_id === undefined || f.wbs_template_id === "__default" ? { ...f, wbs_template_id: preferred.id } : f));
    }).catch(() => {});
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSaving(true);
    try {
      const payload = {
        ...form,
        company_id: form.company_id || null,
        client_id: form.client_id || null,
        contract_value: form.contract_value || null,
        start_date: form.start_date || null,
        target_end_date: form.target_end_date || null,
        dc_capacity_mwp: form.dc_capacity_mwp ? Number(form.dc_capacity_mwp) : null,
        wbs_template_id: form.wbs_template_id && form.wbs_template_id !== "__default" ? form.wbs_template_id : null,
      };
      const { data } = await api.post("/projects", payload);
      toast.success(payload.wbs_template_id
        ? `Project ${data.code} created with its WBS, weights and DPR rows ready`
        : `Project ${data.code} created — add its WBS via Setup Library`);
      setOpen(false);
      onCreated?.();
      navigate(`/projects/${data.id}`);
    } catch (err) {
      setErrors(getFieldErrors(err));
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-project-button"><Plus className="h-4 w-4" /> New Project</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><FolderKanban className="h-4 w-4" /> New Project</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="create-project-form">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Project code</Label><Input placeholder="Automatic if blank" data-testid="project-code-input" value={form.code} onChange={set("code")} className="mt-1.5" /><FieldError error={errors.code} /></div>
            <div><Label>Project name</Label><Input required data-testid="project-name-input" value={form.name} onChange={set("name")} className="mt-1.5" /><FieldError error={errors.name} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <CompanyField label="Company" value={form.company_id} onChange={(v) => setForm({ ...form, company_id: v })} testId="project-company-select" required={false} placeholder="Select company" />
            <div>
              <Label>Client</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="project-client-select" className="mt-1.5"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{partyLabel(c)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Contract value (₹)</Label><Input type="number" step="0.01" data-testid="project-contract-value-input" value={form.contract_value} onChange={set("contract_value")} className="mt-1.5" /></div>
            <div><Label>Location</Label><Input data-testid="project-location-input" value={form.location} onChange={set("location")} className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div />
          </div>
          <div>
            <Label>WBS template <span className="font-normal text-muted-foreground">(builds Engineering, Procurement &amp; Construction automatically)</span></Label>
            <Select value={form.wbs_template_id || "none"} onValueChange={(v) => setForm({ ...form, wbs_template_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="project-template-select" className="mt-1.5"><SelectValue placeholder="Select template" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No template — I'll build the WBS myself</SelectItem>
                {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {form.wbs_template_id && (
              <p className="mt-1 text-[11px] text-muted-foreground">
                {!(templates.find((t) => t.id === form.wbs_template_id)?.name || "").includes("Solar") ? "Quantities are filled from your BOQ after the project is created." : (form.dc_capacity_mwp ? `Quantities per MWp will be scaled for ${form.dc_capacity_mwp} MWp.` : "Enter the DC capacity so quantities per MWp can be calculated.")}
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Start date</Label><Input type="date" data-testid="project-start-date-input" value={form.start_date} onChange={set("start_date")} className="mt-1.5" /></div>
            <div><Label>Target end date</Label><Input type="date" data-testid="project-end-date-input" value={form.target_end_date} onChange={set("target_end_date")} className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-project-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create Project</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
